import React, { useEffect, useRef } from 'react';
import { navigate } from 'gatsby';
import {
  InputGroup,
  InputLeftElement,
  Icon,
  Input,
  chakra,
} from '@chakra-ui/react';
import { MdOutlineSearch } from 'react-icons/md';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import { trackSearchViewedEvent } from '@repo/utils/klaviyo';

interface SearchFormProps {
  currentRefinement?: string | null;
  refine?: (value: string | null) => void;
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  searchFocusUpdater?: number;
  toggleMobileMenu?: (open: boolean) => void;
  numberOfResults?: number;
}

function SearchForm({
  currentRefinement = null,
  refine = () => {},
  onSubmit = event => {
    event.preventDefault();
    navigate(`/search`);
  },
  onFocus = () => {},
  searchFocusUpdater = 1,
  toggleMobileMenu = () => {},
  numberOfResults = 0,
}: Readonly<SearchFormProps>) {
  const searchRef = useRef<HTMLInputElement>(null);

  const { tt } = useUiComponentsInternationalContext();
  useEffect(() => {
    if (searchFocusUpdater > 1) {
      if (searchRef.current) searchRef.current.focus();
    }
  }, [searchFocusUpdater]);

  const lastTrackedQuery = useRef<string | null>(null);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    onFocus(e);
    toggleMobileMenu(true);
  };

  return (
    <chakra.form
      onSubmit={onSubmit}
      noValidate
      role="search"
      action="."
      minW={{ base: '2xs', xl: 'xs' }}
      w="100%"
      outline="none"
      border="none"
    >
      <InputGroup
        onFocus={handleFocus}
        background="primary.extraDark"
        className="search-input-group"
        borderRadius="lg"
        overflow="hidden"
        border="1px solid rgba(255, 255, 255, 0.30)"
      >
        <InputLeftElement pointerEvents="none" ml="2">
          <Icon as={MdOutlineSearch} color="gray.300" w="6" h="6" />
        </InputLeftElement>
        <Input
          type="text"
          placeholder={tt(s => s.headerCommon.searchPlaceholder)}
          value={currentRefinement ?? ''}
          onKeyDown={e => {
            if (
              e.key === 'Enter' &&
              currentRefinement &&
              currentRefinement !== lastTrackedQuery.current
            ) {
              lastTrackedQuery.current = currentRefinement;
              trackSearchViewedEvent(currentRefinement, numberOfResults);
            }
          }}
          onChange={e => {
            if (e.currentTarget.value?.length) {
              refine(e.currentTarget.value);
            } else {
              refine(null);
            }
          }}
          background="primary.extraDark"
          border="none"
          ref={searchRef}
          color="typography.lighter"
          pl="12"
          _placeholder={{
            color: 'typography.lighter',
            textStyle: 'body',
            position: 'relative',
            bottom: '1px',
          }}
        />
      </InputGroup>
    </chakra.form>
  );
}

export default SearchForm;
